// Funding › Receivables (docs/PARTNER_API.md § 4.5): what partners owe QOR for advances, by age (current, 1–7, 8–30,
// 31–90, 90+ days overdue), written-off debt still chased, and each collection case: who chases it, where it stands,
// the partner's promise, and every contact noted. Any QOR staff member works a case.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AGING_BUCKETS, fundsApi, type CollectionStatus, type Receivables } from '../api';

const STATUS_LABEL: Record<CollectionStatus, string> = {
  open: 'Open',
  promised: 'Promised to pay',
  disputed: 'Disputed',
  doubtful: 'Doubtful',
  written_off: 'Written off',
  closed: 'Closed',
};
const BUCKET_LABEL: Record<string, string> = {
  current: 'Not due yet',
  '1-7': '1–7 days',
  '8-30': '8–30 days',
  '31-90': '31–90 days',
  '90+': 'Over 90 days',
};
const bucketBadge = (b: string) =>
  b === 'current' ? 'qc-badge-muted' : b === '1-7' ? 'qc-badge-warning' : 'qc-badge-danger';

type Row = Receivables['items'][number];

function CaseCell({
  row,
  staff,
  busy,
  onSaved,
}: {
  row: Row;
  staff: { id: string; email: string }[];
  busy: boolean;
  onSaved: (ok: boolean, message: string) => void;
}) {
  const c = row.collection;
  const [note, setNote] = useState('');
  const [promised, setPromised] = useState(c?.promisedAt?.slice(0, 10) ?? '');
  if (!c) return <small>No case (not overdue)</small>;
  async function update(body: Parameters<typeof fundsApi.updateCollection>[1], what: string) {
    const r = await fundsApi.updateCollection(c!.id, body);
    onSaved(r.ok, r.ok ? what : r.error);
  }
  async function addNote() {
    if (!note.trim()) return;
    const r = await fundsApi.addCollectionNote(c!.id, note.trim());
    if (r.ok) setNote('');
    onSaved(r.ok, r.ok ? 'Note added.' : r.error);
  }
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 260 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <select
          aria-label={`Status of ${row.name}`}
          value={c.status}
          disabled={busy}
          onChange={(e) => {
            const status = e.target.value as CollectionStatus;
            if (status === 'promised' && !promised) return onSaved(false, 'Set the promised date first.');
            void update(
              { status, ...(status === 'promised' && { promisedAt: new Date(`${promised}T23:59:59Z`).toISOString() }) },
              `Status: ${STATUS_LABEL[status]}.`,
            );
          }}
        >
          {(['open', 'promised', 'disputed', 'doubtful', 'written_off'] as const).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          aria-label={`Who chases ${row.name}`}
          value={c.assignee?.id ?? ''}
          disabled={busy}
          onChange={(e) =>
            void update(
              { assigneeId: e.target.value || null },
              e.target.value ? 'Assigned.' : 'Nobody is assigned now.',
            )
          }
        >
          <option value="">Nobody assigned</option>
          {staff.map((s) => (
            <option key={s.id} value={s.id}>
              {s.email}
            </option>
          ))}
        </select>
      </div>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <small>Promised for</small>
        <input
          type="date"
          value={promised}
          disabled={busy}
          onChange={(e) => {
            setPromised(e.target.value);
            if (e.target.value)
              void update(
                { promisedAt: new Date(`${e.target.value}T23:59:59Z`).toISOString() },
                'Promised date saved.',
              );
          }}
        />
      </label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          placeholder="What was done (a call, an e-mail…)"
          aria-label={`Note for ${row.name}`}
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="button"
          className="qc-button-ghost"
          disabled={busy || !note.trim()}
          onClick={() => void addNote()}
        >
          Add note
        </button>
      </div>
      {c.notes.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {c.notes.slice(0, 3).map((n) => (
            <li key={n.id}>
              <small>
                {shortDate(n.createdAt)} · {n.by}: {n.note}
              </small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ReceivablesPanel({ onDone }: { onDone: (ok: boolean, message: string) => void }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const receivables = useQuery({ queryKey: ['receivables'], queryFn: () => fundsApi.receivables() });
  const staff = useQuery({ queryKey: ['qor-staff'], queryFn: () => fundsApi.qorStaff() });

  async function saved(ok: boolean, message: string) {
    setBusy(false);
    onDone(ok, message);
    await client.invalidateQueries({ queryKey: ['receivables'] });
  }

  const r = receivables.data;
  return (
    <div className="qc-panel" style={{ marginTop: 20 }}>
      <div className="qc-panel-title">
        <h3>Receivables</h3>
        <span>What partners owe QOR for advances, by how long it is overdue, and who is chasing it.</span>
      </div>
      {!r || r.items.length === 0 ? (
        <p className="qc-empty">No partner owes QOR anything.</p>
      ) : (
        <>
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Currency</th>
                {AGING_BUCKETS.map((b) => (
                  <th key={b}>{BUCKET_LABEL[b]}</th>
                ))}
                <th>Total owed</th>
                <th>Written off (still chased)</th>
              </tr>
            </thead>
            <tbody>
              {r.totals.map((t) => (
                <tr key={t.currency}>
                  <td>{t.currency}</td>
                  {AGING_BUCKETS.map((b) => (
                    <td key={b}>{t.buckets[b]}</td>
                  ))}
                  <td>
                    <strong>{t.outstanding}</strong>
                  </td>
                  <td>{t.writtenOff}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="qc-plain-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Owed</th>
                <th>Due</th>
                <th>Overdue</th>
                <th>Collection</th>
              </tr>
            </thead>
            <tbody>
              {r.items.map((row) => (
                <tr key={row.accountId}>
                  <td>
                    <Link to={`/partners/${row.programId}?tab=funding`}>{row.name}</Link>
                  </td>
                  <td>
                    <strong>
                      {row.outstanding} {row.currency}
                    </strong>
                    {row.writtenOff !== '0.00' && (
                      <>
                        <br />
                        <small>written off {row.writtenOff}</small>
                      </>
                    )}
                  </td>
                  <td>{row.nextDueAt ? shortDate(row.nextDueAt) : '—'}</td>
                  <td>
                    {row.outstanding === '0.00' ? (
                      <span className="qc-badge qc-badge-danger">Written off</span>
                    ) : (
                      <span className={`qc-badge ${bucketBadge(row.bucket)}`}>
                        {row.daysOverdue === null ? BUCKET_LABEL.current : `${row.daysOverdue} days`}
                      </span>
                    )}
                  </td>
                  <td>
                    <CaseCell
                      key={row.collection?.id ?? row.accountId}
                      row={row}
                      staff={staff.data ?? []}
                      busy={busy}
                      onSaved={(ok, message) => void saved(ok, message)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
